import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { adminService } from '../../../services/subscriptionService';
import { AdminUser } from '../../../types';

interface Props {
  user: AdminUser;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const GrantSubscriptionDialog = ({ user, open, onClose, onSuccess }: Props) => {
  const { t } = useTranslation();
  const [months, setMonths] = useState(1);
  const [notes, setNotes] = useState('');

  const mutation = useMutation({
    mutationFn: () => adminService.grantSubscription(user.id, months, notes || undefined),
    onSuccess,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('admin.grantDialog.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('admin.grantDialog.subtitle', { name: `${user.first_name} ${user.last_name}` })}
        </Typography>
        <TextField
          label={t('admin.grantDialog.months')}
          type="number"
          value={months}
          onChange={(e) => setMonths(Math.max(1, parseInt(e.target.value) || 1))}
          inputProps={{ min: 1, max: 36 }}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label={t('admin.grantDialog.notes')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={2}
          fullWidth
          placeholder={t('admin.grantDialog.notesPlaceholder')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          color="success"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} /> : undefined}
        >
          {t('admin.grantDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
